import { inject } from '@angular/core';
import { type WritableDraft } from 'immer';
import { type Observable, of } from 'rxjs';

import { type CanvasElementFrom, type PartialPropsOf } from '@blaze-runtime/engine-domain';
import { applyPropsPatch } from '@blaze-runtime/engine-domain';

import { type EngineState } from '../../../+state/engine';
import {
  type AiChildGroup,
  type ElementHandler,
  getElement,
  getInitialProps,
  IconResolverService,
  upsertElement,
} from '../../../ai';
import { toWidthSettings } from '../../../ai/models/styles';

import {
  toEngineAlignment,
  toEngineIconPosition,
  toEngineTabSize,
  toEngineTemplate,
} from './tabs.appearance';
import { type TabsContext, type TabsDefinition } from './tabs.definition';

export class TabsElementHandler implements ElementHandler<'tabs'> {
  private readonly iconResolverService = inject(IconResolverService);
  readonly type = 'tabs';

  getChildGroups(element: TabsDefinition): AiChildGroup[] {
    return (element.tabs ?? []).map((tab, index) => ({
      slotId: `${index}`,
      children: tab.children,
    }));
  }

  prepare(context: TabsContext): Observable<TabsContext> {
    if (context.element.tabs) {
      for (const tab of context.element.tabs) {
        if (tab.iconName) {
          tab.iconName = this.iconResolverService.resolveFullIconName(tab.iconName);
        }
      }
    }

    return of(context);
  }

  process(context: TabsContext, draft: WritableDraft<EngineState>): void {
    const props = this.getTabsProps(context, draft);
    const result = upsertElement(draft, context, props, false);
    const element = getElement(draft, result.elementId, 'tabs');

    this.updateSlots(context, element);
  }

  private updateSlots(context: TabsContext, element: CanvasElementFrom<'tabs'>): void {
    if (Array.isArray(context.element.tabs)) {
      element.slots = {
        ids: [],
      };

      context.element.tabs.forEach((tab, index) => {
        element.slots.ids.push(`${index}`);
        element.slots[`${index}`] = {
          children: [],
          props: {},
        };
      });
    }

    if (context.element.width) {
      element.width = toWidthSettings(context.element.width);
    } else {
      element.width = {
        mode: 'full',
      };
    }
  }

  private getTabsProps(
    context: TabsContext,
    draft: WritableDraft<EngineState>,
  ): PartialPropsOf<'tabs'> {
    const { element } = context;

    let props: PartialPropsOf<'tabs'> = getInitialProps<'tabs'>(draft, context.element.id, {
      content: {
        tabs: {
          items: [],
        },
      },
    });

    if (element.template) {
      props = applyPropsPatch(props, {
        content: {
          options: {
            template: toEngineTemplate(element.template),
          },
        },
      });
    }

    if (element.tabSize) {
      props = applyPropsPatch(props, {
        content: {
          options: toEngineTabSize(element.tabSize),
        },
      });
    }

    if (element.alignment) {
      props = applyPropsPatch(props, {
        content: {
          options: {
            align: toEngineAlignment(element.alignment),
          },
        },
      });
    }

    if (element.iconPosition) {
      props = applyPropsPatch(props, {
        content: {
          options: {
            iconPosition: toEngineIconPosition(element.iconPosition),
          },
        },
      });
    }

    if (element.tabs) {
      props = applyPropsPatch(props, {
        content: {
          tabs: {
            items: element.tabs.map((tab, index) => ({
              id: index,
              label: tab.label ?? `Tab ${index + 1}`,
              icon: tab.iconName,
            })),
          },
        },
      });
    }

    return props;
  }
}
